import { NAVIGATION_BUTTON_TYPES } from 'containers/NavigationButtonContainer';
import { getFirstInteractiveLinkId, getVisiblePagesAmount } from 'helper/js/helper';
import { useFlippyPageNumber } from 'helper/js/hooks/router';
import { createContext, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

export const InitialFocusContext = createContext<string | null>(null);

export const useInitialFocus = () => {
  const context = useContext(InitialFocusContext);
  return context;
};

interface InitialFocusProviderProps {
  children: React.ReactNode;
}

export const InitialFocusProvider: React.FC<InitialFocusProviderProps> = ({ children }) => {
  const allPages = useSelector((state: AppState) => state.appData.pages);
  const flippyPageNumber = useFlippyPageNumber();
  const isWideScreen = useSelector((state: AppState) => state.browser.isWideScreen);

  const allLinksOnVisibleSpread = useMemo(() => {
    let linksToConsider: ALink[] = [];
    const leftPageData = allPages.find((p) => Number(p.number) === flippyPageNumber);
    if (leftPageData) {
      linksToConsider = leftPageData.links.map((link) => {
        return {
          ...link,
          pageNumber: flippyPageNumber,
        };
      });
    }

    if (isWideScreen) {
      const rightPageNumber = flippyPageNumber + 1;
      const rightPageData = allPages.find((p) => Number(p.number) === rightPageNumber);
      if (rightPageData) {
        const linksWithPageNumber = rightPageData.links.map((link) => {
          return { ...link, pageNumber: rightPageNumber };
        });
        linksToConsider = [...linksToConsider, ...linksWithPageNumber];
      }
    }

    return linksToConsider;
  }, [allPages, flippyPageNumber, isWideScreen]);

  const firstInteractiveElementId = useMemo(() => {
    const firstLinkId = getFirstInteractiveLinkId(allLinksOnVisibleSpread);
    if (firstLinkId) {
      return firstLinkId;
    }

    const totalPages = allPages.length;
    const currentVisible = getVisiblePagesAmount(flippyPageNumber, totalPages, isWideScreen);

    const isFirstPage = flippyPageNumber === 1;
    const isLastPage =
      flippyPageNumber === totalPages ||
      (currentVisible === 2 && totalPages > 2 && flippyPageNumber === totalPages - 1);

    if (totalPages > currentVisible) {
      if (isFirstPage) {
        return `nav-button-${NAVIGATION_BUTTON_TYPES.forward}`;
      }
      if (isLastPage) {
        return `nav-button-${NAVIGATION_BUTTON_TYPES.backward}`;
      }

      return `nav-button-${NAVIGATION_BUTTON_TYPES.forward}`;
    }

    return null;
  }, [allLinksOnVisibleSpread, allPages.length, flippyPageNumber, isWideScreen]);

  return (
    <InitialFocusContext.Provider value={firstInteractiveElementId}>
      {children}
    </InitialFocusContext.Provider>
  );
};
